import * as Ariakit from '@ariakit/react';
import { styled } from 'styled-components';
import { ReactNode, useMemo } from 'react';

import { MenuCloseIcon, MenuOpenIcon } from '../../../assets/icons';
import { topBarHeight } from '../common/commonStyles';
import { useIsDeviceSize } from '../../Media';
import { zIndex } from '../../../styles/zIndex';
import { NavbarHamburgerMenuContext } from './contexts';
import { unsetButtonStyle } from '../../../utils/unsetButtonStyle';

export const NavbarHamburgerMenu = ({ children }: { children: ReactNode }) => {
  const menuStore = Ariakit.useMenuStore({ defaultOpen: false, placement: 'bottom' });
  const menuOpen = Ariakit.useStoreState(menuStore, 'open');

  const { isMobile: isSmall } = useIsDeviceSize();

  // Resize observer loop errors occur if this is used inside the updatePosition function. Only update when it opens/closes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const topDisplacement = useMemo(() => topBarHeight[isSmall ? 'mobile' : 'default'], [menuOpen]);

  const updatePosition = () => {
    // Change menu popover positioning. Example from: https://ariakit.org/examples/popover-responsive
    const { popoverElement, mounted } = menuStore.getState();
    if (!popoverElement) return;
    Object.assign(popoverElement.style, {
      display: mounted ? 'block' : 'none',
      position: 'fixed',
      bottom: '0px',
      width: 'calc(100% - var(--scrollbar-width, 0))',
      top: topDisplacement + 'px',
      zIndex: zIndex.modal,
    });
  };

  return (
    <NavbarHamburgerMenuContext.Provider value={{ inNavbarHamburgerMenu: true }}>
      <Ariakit.MenuProvider store={menuStore}>
        <AriaMenuButton aria-label="Menu">
          {menuOpen ? <MenuCloseIcon width={iconSize} /> : <MenuOpenIcon width={iconSize} />}
        </AriaMenuButton>
        <AriaMenu preventBodyScroll updatePosition={updatePosition} unmountOnHide>
          {children}
        </AriaMenu>
      </Ariakit.MenuProvider>
    </NavbarHamburgerMenuContext.Provider>
  );
};

const iconSize = 22;

const AriaMenuButton = styled(Ariakit.MenuButton)`
  ${unsetButtonStyle}
  display: flex;
  align-items: center;
  height: 100%;
`;

const AriaMenu = styled(Ariakit.Menu)`
  background-color: white;
  overflow-y: auto;

  height: 0;
  transition: height 0.2s ease-in-out;
  &[data-enter] {
    height: 100%;
  }
`;
